/**
 * The question asked about hiring when someone is deployed to a store (product owner, 21 Oct
 * 2026: "when we are redeploying any other employee to that store where hiring is going on ...
 * currently it does not ask whether we need to cancel the hiring, stop the hiring process, or
 * move that hiring to another store", from the Employees page, the Stores page and the
 * Dashboard's redeploy alike). Since 22 Oct 2026 it is asked BEFORE the deploy is saved ("when we
 * are assigning an employee or deploying at the store - so before that deployment saved - it
 * should check whether any hiring is going on or not - If yes then show immediately that pop up
 * box and then decide and save the deployment"). Driven by `useDeployHiringCheck`, in three stages:
 *
 * BEFORE THE SAVE ('asking'). The store someone joins (both stores of a two-way swap) is read: its
 * live openings, and any hiring request waiting there. A person not shown hiring, a store with
 * neither, a read that fails, or reads not back within DEPLOY_HIRING_WAIT_MS: no window at all,
 * and the page deploys at once, exactly as it did. A deploy is never held up by a read.
 * Otherwise ONE window: "Hiring is open at <store>" ("Check hiring", with a part per store, for a
 * swap), "You’re deploying <person> here. What should happen to this hiring?", and each opening on
 * one line ("Sample Promoter · Opening 48213 · 2 open · 5 candidates") with its CHOICE, nothing
 * happening as it is picked: Keep hiring (chosen to start with), Move to another store, Cancel
 * opening, Stop following, each only where the opening's own window would offer it to the person.
 * A waiting request: Keep request, Withdraw (the person's own) or Decline (whoever may decline).
 * A person with Hiring (view) alone gets the notice and no choice. "Don’t deploy" closes it and
 * nothing is saved (the page's deploy window is as it was); "Deploy" lets the page save.
 *
 * WHILE THE PAGE SAVES ('saving') the window stays, its buttons off and "Deploying…" on the
 * primary. A deploy that FAILS does nothing to hiring: the page says its own error, and this goes.
 *
 * ONCE IT IS SAVED ('saved'), in this order (owner's decision, 22 Oct 2026: "Save deploy, then
 * finish Move/Cancel"; the deploy stands whatever happens next):
 *   1. every "Stop following" is sent at once (the release route the opening's window uses), and
 *      what became of each is said in the window ("Stopped following Opening N", or why not);
 *   2. each Move and Cancel opens the opening's own window (`HiringPanel`) with that step started,
 *      one opening at a time, under "<person> is deployed. Now finish moving Opening N.": its own
 *      review, refusals (1Office's rule of who may move an opening among them) and confirmation.
 *      Closing it goes on to the next;
 *   3. each Withdraw and Decline opens the hiring request's window on that question;
 *   4. then "<store left> needs someone", as on 21 Oct 2026, when the person moved OUT of a store
 *      that is now short, follows nothing and has no request waiting, was not marked Outside
 *      Approved – Redeploy before the move, Raise is on for the project and the person may raise
 *      (or ask for) hiring there: "Raise hiring" (or "Ask for hiring") and "Not now". Whether it is
 *      short is read from the store as the move left it (`leftStoreNeedOf`), so only after the
 *      save; where that cannot be told the heading is an "if". Its mark is told by the page, from
 *      the store as it was: the server clears it once the last person leaves.
 * With nothing to say after the save (every choice Keep, no store left to ask about) it just goes.
 *
 * NOTHING HERE DECIDES A RULE. Move, Cancel, Withdraw, Decline and Raise are each the window that
 * already does them, with its own checks and confirmation; Stop following is the very call that
 * window makes, its refusals said as that window says them.
 *
 * What it reads: the access answer; each store's live links, one small read per store
 * (`readLiveStoreLinks` with its id, never the project's whole list; on the Stores page the page's
 * own list, `known`, when it is read, and no request); the waiting hiring requests (the shared
 * list, `useHiringRequests`); and, after the save, the store left (`GET /stores/:id`, which leaves
 * out salaries and the payout band for a person without the money grant, as GET /stores does).
 * After any action the window's lists are corrected at once from the answer and read again, and
 * the page is told (`onChanged`), as the page's own windows tell it.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import Modal from '../Modal';
import { hiringLinksAPI, storesAPI, type HiringLink, type HiringRequest } from '../../services/api';
import { storeDisplayStatus } from '../../constants/storeStatus';
import { HiringPanel } from './HiringPanel';
import { HiringRequestWindow } from './HiringRequestWindow';
import { LinkHiringModal } from './LinkHiringModal';
import { RaiseHiringModal, type RaiseHiringSubject } from './RaiseHiringModal';
import {
  CANCEL_CODES, CANCEL_WORDS, HIRING_CODES, hiringCodeOf, hiringErrorText, hiringStatusOf, isHiringOff, openingTitle,
} from './hiringText';
import {
  DEPLOY_CHECK_WORDS as WORDS, deployOpeningLine, deployOpeningRef, deployRequestLine, leftStoreNeedOf, type LeftStoreNeed,
} from './hiringText.deploy';
import { forgetHiringAccess, useHiringAccess } from './useHiringAccess';
import { forgetHiringRequests, useHiringRequests } from './useHiringRequests';
import {
  NO_CANCEL, NO_HIRING_LINKS, NO_RAISE, readLiveStoreLinks, storeHiringKey, withLink, type CancelAccess, type RaiseAccess,
} from './useStoreHiring';
import { storeRaiseRefusal } from './useStoreRowHiring';

/** How long the reads may take before the deploy goes ahead with no question (about 5 s). */
export const DEPLOY_HIRING_WAIT_MS = 5000;

/** A store someone is being deployed to, and who. */
export interface DeployHiringJoined {
  storeId: string;
  storeName: string;
  personName: string;
  /** The store will still be below its minimum with them in it: "Keep hiring" says so on its hover. Null or absent: not known. */
  short?: boolean | null;
}

/** The store a person moves out of, when the page knows it (a move of a single-store person). */
export interface DeployHiringLeft {
  storeId: string;
  storeName: string;
  personName: string;
  /** Their employee code: a store they are still deployed at (a roaming role keeps its stores) was not left. */
  personCode?: string | null;
  /**
   * The store was marked Outside Approved – Redeploy BEFORE the move: its people are being moved
   * out on purpose, so no hiring is offered for it. The page tells it from the store as it held
   * it; the server clears the mark once the last person leaves, so the read after the move cannot.
   */
  redeployPending?: boolean | null;
}

/** The page's own read of the project's live store hiring (the Stores page's list), when it is read. */
export interface DeployHiringKnown {
  /** Live links keyed by store id in lower case; a store not in it follows nothing. */
  byStore: ReadonlyMap<string, HiringLink[]>;
  raise: RaiseAccess;
  cancel: CancelAccess;
}

/** What one deploy asks about: the store, or the two stores of a two-way swap; and the store left, if any. */
export interface DeployHiringAsk {
  joined: readonly DeployHiringJoined[];
  /** Asked about only once the deploy is saved: whether it is now short needs the save. */
  left?: DeployHiringLeft | null;
  /** The stores joined are read from this, with no request, where it is given. */
  known?: DeployHiringKnown | null;
}

/** Where the deploy stands: asked about with nothing saved, being saved by the page, or saved. */
export type DeployHiringStage = 'asking' | 'saving' | 'saved';

/** What is chosen for an opening, or for a waiting request. Keep is chosen to start with. */
type Choice = 'keep' | 'move' | 'cancel' | 'stop' | 'withdraw' | 'decline';

/** One thing to do once the deploy is saved, as it was chosen: Stop following is sent at once... */
type StopStep = { kind: 'stop'; storeId: string; storeName: string; personName: string; link: HiringLink };
/** ...and each of these is finished in its own window, one at a time. */
type OpeningStep = { kind: 'move' | 'cancel'; storeId: string; storeName: string; personName: string; link: HiringLink };
type RequestStep = { kind: 'withdraw' | 'decline'; storeId: string; personName: string; request: HiringRequest };
type Step = StopStep | OpeningStep | RequestStep;
type WindowStep = OpeningStep | RequestStep;

type ReadState = 'loading' | 'ready' | 'failed';

interface Lists {
  read: Readonly<Record<string, ReadState>>;
  /** Each store's live links, keyed by store id in lower case. */
  byStore: ReadonlyMap<string, HiringLink[]>;
  raise: RaiseAccess;
  cancel: CancelAccess;
}

/** The store left, as GET /stores/:id answers it. */
type StoreRow = Record<string, unknown>;
type LeftRead = { state: 'loading' } | { state: 'ready'; row: StoreRow } | { state: 'failed' };

type Sub =
  | { kind: 'store'; storeId: string; storeName: string }
  | { kind: 'request'; request: HiringRequest }
  | { kind: 'raise' | 'ask'; subject: RaiseHiringSubject }
  | { kind: 'link'; subject: RaiseHiringSubject; pick: { budgetId: number; openingId: number } };

/** A "Stop following" sent once the deploy was saved: what became of it. */
interface StopLine { id: string; state: 'sending' | 'done' | 'failed'; text: string; next?: string }

const NO_REQUESTS: readonly HiringRequest[] = [];
const LEFT_PART = 'left';

const text = (value: unknown): string => String(value ?? '').trim();
const linkKey = (link: HiringLink): string => `link:${link.id}`;
const requestKey = (request: HiringRequest): string => `request:${request.id}`;

/** The store left, as the Raise window's store line shows it. */
const leftSubjectOf = (row: StoreRow, storeId: string, storeName: string): RaiseHiringSubject => ({
  kind: 'store',
  storeId,
  storeName: text(row.store_name) || storeName,
  storeCode: text(row.store_code),
  address: text(row.address),
  city: text(row.city),
  state: text(row.state),
  pincode: text(row.pin_code),
  hasMapLocation: !!text(row.latitude) && !!text(row.longitude),
  status: storeDisplayStatus(row as { status_display?: string | null; store_status?: string | null }),
  staffingStatus: text(row.staffing_status),
});

/** The store code a store's links carry; the windows' titles read "Name (CODE)" with it. */
const storeCodeOf = (links: readonly HiringLink[]): string => {
  for (const link of links) {
    if (link.subject.kind === 'store' && text(link.subject.storeCode)) return text(link.subject.storeCode);
  }
  return '';
};

/** The page's list, as this window keeps it, for the stores joined: read, with no request. */
const listsFromKnown = (known: DeployHiringKnown | null | undefined, joined: readonly DeployHiringJoined[]): Lists => {
  if (!known) return { read: {}, byStore: new Map(), raise: NO_RAISE, cancel: NO_CANCEL };
  const read: Record<string, ReadState> = {};
  const byStore = new Map<string, HiringLink[]>();
  for (const store of joined) {
    const key = storeHiringKey(store.storeId);
    read[key] = 'ready';
    byStore.set(key, (known.byStore.get(key) ?? NO_HIRING_LINKS).filter((l) => l.subject?.kind === 'store' && storeHiringKey(l.subject.storeId) === key));
  }
  return { read, byStore, raise: known.raise, cancel: known.cancel };
};

/**
 * One store's live links into the window's lists. A re-read that fails keeps what was read; a
 * store never read is "failed", and gets no part. Inside the chain: a throw while building the
 * request must never reach the page.
 */
const readStoreInto = (
  projectId: number, storeId: string, live: () => boolean, setLists: Dispatch<SetStateAction<Lists>>,
): void => {
  const key = storeHiringKey(storeId);
  Promise.resolve()
    .then(() => readLiveStoreLinks(projectId, live, storeId))
    .then(({ links, raise, cancel }) => {
      if (!live()) return;
      setLists((prev) => {
        const byStore = new Map(prev.byStore);
        byStore.set(key, links.filter((l) => l.subject?.kind === 'store' && storeHiringKey(l.subject.storeId) === key));
        return { read: { ...prev.read, [key]: 'ready' }, byStore, raise, cancel };
      });
    })
    .catch((err: unknown) => {
      if (!live()) return;
      if (isHiringOff(err) || hiringStatusOf(err) === 403) forgetHiringAccess(projectId);
      setLists((prev) => (prev.read[key] === 'ready' ? prev : { ...prev, read: { ...prev.read, [key]: 'failed' } }));
    });
};

// A choice is a radio, in the look of the hiring form's segmented control, wrapping on a phone.
const CHOICE = 'whitespace-nowrap rounded-lg border px-3 py-1.5 text-[13px] transition-colors disabled:opacity-60';
const CHOICE_ON = 'border-primary-600 bg-primary-50 font-semibold text-primary-800 ring-1 ring-inset ring-primary-600';
const CHOICE_OFF = 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50';
const PRIMARY = 'rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-60';
const SECONDARY = 'rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60';
const OPEN_LINK = 'whitespace-nowrap text-[13px] font-medium text-primary-600 underline decoration-primary-200 underline-offset-2 hover:text-primary-700 disabled:opacity-60';

function Choices({ label, options, value, onChange, disabled }: {
  label: string;
  options: ReadonlyArray<{ value: Choice; label: string; hover: string }>;
  value: Choice;
  onChange: (value: Choice) => void;
  disabled: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="mt-2 flex flex-wrap gap-1.5">
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            title={option.hover}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`${CHOICE} ${on ? CHOICE_ON : CHOICE_OFF}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function DeployHiringCheck({
  projectId, userKey, ask, stage, onDecide, onDone, onChanged, onPlaced,
}: {
  projectId: number;
  userKey: string;
  ask: DeployHiringAsk;
  stage: DeployHiringStage;
  /**
   * Before the save: true lets the page save the deploy (Deploy pressed, or nothing to ask);
   * false is "Don’t deploy" (nothing is saved). Said once.
   */
  onDecide: (deploy: boolean) => void;
  /** Nothing is left to say after the save, or the person closed the window: the owner lets go of it. */
  onDone: () => void;
  /**
   * The page's own lists may be behind: with the link as the server just answered it (stopped,
   * moved, cancelled, raised, confirmed), or with nothing when they should simply be read again.
   */
  onChanged?: (link?: HiringLink) => void;
  /** A hiring was closed as filled from an opening's window: someone was placed at a store. */
  onPlaced?: () => void;
}) {
  const access = useHiringAccess(projectId, userKey);
  const shown = access.canView;
  const requests = useHiringRequests(projectId, userKey, shown && access.canSeeRequests);
  const saved = stage === 'saved';

  const joined = ask.joined;
  const joinedKey = joined.map((j) => j.storeId).join('|');
  // The store left is asked about only once the deploy is saved, only when it is not also a store
  // someone joined (a swap has none), and never when it was marked Outside Approved – Redeploy
  // before the move (emptied on purpose): neither is read.
  const left = ask.left && ask.left.redeployPending !== true
    && !joined.some((j) => storeHiringKey(j.storeId) === storeHiringKey(ask.left?.storeId)) ? ask.left : null;
  const leftId = saved ? left?.storeId ?? null : null;
  // The stores the page's list already answers for: not read again until something changes here.
  const knownKeys = useRef(new Set(ask.known ? joined.map((j) => storeHiringKey(j.storeId)) : []));

  const [lists, setLists] = useState<Lists>(() => listsFromKnown(ask.known, joined));
  const [reads, setReads] = useState(0);
  const [leftRead, setLeftRead] = useState<LeftRead>({ state: 'loading' });
  // "Not now" for the store left.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  // Raising was switched off under a window: the part about the store left goes.
  const [raiseGone, setRaiseGone] = useState(false);
  const [sub, setSub] = useState<Sub | null>(null);
  // What each opening and request is set to; Keep where nothing was picked.
  const [choices, setChoices] = useState<Readonly<Record<string, Choice>>>({});
  // What was decided before the save: whether the window was shown, and what to do once saved.
  const [decision, setDecision] = useState<{ shown: boolean; steps: readonly Step[] } | null>(null);
  // The reads took longer than the deploy waits for.
  const [waitedOut, setWaitedOut] = useState(false);
  // Once saved: each Stop following, and the windows still to open (one at a time).
  const [stops, setStops] = useState<readonly StopLine[]>([]);
  const [queue, setQueue] = useState<readonly WindowStep[] | null>(null);

  // The stores joined: one small read each, again after any action here.
  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    for (const storeId of joinedKey.split('|').filter(Boolean)) {
      if (reads === 0 && knownKeys.current.has(storeHiringKey(storeId))) continue;
      readStoreInto(projectId, storeId, () => !cancelled, setLists);
    }
    return () => {
      cancelled = true;
    };
  }, [projectId, shown, joinedKey, reads]);

  // The store left, once the deploy is saved: its live links, again after any action here...
  useEffect(() => {
    if (!shown || !leftId) return undefined;
    let cancelled = false;
    readStoreInto(projectId, leftId, () => !cancelled, setLists);
    return () => {
      cancelled = true;
    };
  }, [projectId, shown, leftId, reads]);

  // ...and the store as the move left it: whether it needs someone, and its Raise window's store line.
  useEffect(() => {
    if (!shown || !leftId) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => storesAPI.getById(leftId))
      .then((res) => {
        if (cancelled) return;
        const row = res?.data?.data as StoreRow | undefined;
        setLeftRead(row && typeof row === 'object' && row.id ? { state: 'ready', row } : { state: 'failed' });
      })
      .catch(() => {
        if (!cancelled) setLeftRead({ state: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [shown, leftId]);

  const reloadRequests = requests.reload;
  const reloadAll = useCallback(() => {
    setReads((n) => n + 1);
    reloadRequests();
  }, [reloadRequests]);
  const apply = useCallback((link: HiringLink) => {
    setLists((prev) => ({ ...prev, byStore: withLink(prev.byStore, link) }));
  }, []);
  // The page is told, as its own windows tell it; a hiring closed as filled placed someone.
  const tell = (link?: HiringLink) => {
    onChanged?.(link);
    if (link?.closedAs === 'filled') onPlaced?.();
  };
  /** A window here changed something: corrected at once from the answer, read again, and the page told. */
  const changed = (link?: HiringLink) => {
    if (link) apply(link);
    reloadAll();
    tell(link);
  };
  const changedNow = useRef(changed);
  changedNow.current = changed;
  const hiringGone = () => forgetHiringAccess(projectId);
  const raiseGoneNow = () => {
    setRaiseGone(true);
    forgetHiringRequests(projectId);
    setSub(null);
    reloadAll();
    tell();
  };

  const linksOf = (storeId: string): readonly HiringLink[] =>
    (lists.byStore.get(storeHiringKey(storeId)) ?? NO_HIRING_LINKS).filter((l) => l.state !== 'closed');
  const requestsShown = requests.status === 'ready';
  const requestOf = (storeId: string): HiringRequest | null =>
    (requestsShown ? requests.byStore.get(storeHiringKey(storeId)) ?? null : null);

  // Nothing is said before every read is back.
  const readIn = (storeId: string): boolean => {
    const state = lists.read[storeHiringKey(storeId)];
    return state === 'ready' || state === 'failed';
  };
  const decided = access.state !== 'unknown' && (!shown || (
    joined.every((store) => readIn(store.storeId)) && requests.status !== 'loading'
  ));
  const leftDecided = access.state !== 'unknown' && (!shown || !left || (
    readIn(left.storeId) && leftRead.state !== 'loading' && requests.status !== 'loading'
  ));

  // Who may do what, as the opening's window and the Stores page decide it.
  const mayCancel = access.canManage && access.canRaise && lists.cancel.canCancel;
  const canRaise = access.canRaise && lists.raise.canRaise && !raiseGone;
  const canAsk = requestsShown && requests.can.send && !access.canRaise && !raiseGone;
  const choicesOf = (link: HiringLink) => {
    const live = link.state === 'live';
    const cancellable = live && mayCancel && !link.shared && link.openingId != null && !link.cancel;
    return {
      move: cancellable && link.subject.kind === 'store' && !!link.subject.storeId,
      cancel: cancellable,
      stop: live && access.canManage,
    };
  };
  const requestChoicesOf = (request: HiringRequest) => ({
    withdraw: request.standing === 'waiting' && request.mine,
    decline: request.standing === 'waiting' && requests.can.decline && !request.mine,
  });

  // Before the save: each store someone joins that has hiring, and whether anything there is the person's to decide.
  const openParts = decided && shown
    ? joined.flatMap((store) => {
      const key = storeHiringKey(store.storeId);
      if (lists.read[key] !== 'ready') return [];
      const links = linksOf(store.storeId);
      const request = requestOf(store.storeId);
      if (links.length === 0 && !request) return [];
      const asks = request ? requestChoicesOf(request) : null;
      const anyChoice = links.some((link) => Object.values(choicesOf(link)).some(Boolean))
        || (!!asks && (asks.withdraw || asks.decline));
      return [{ store, key, links, request, asks, anyChoice }];
    })
    : [];

  // After the save: the store left, only when everything about it is known and says it may need someone.
  const leftPart = ((): { left: DeployHiringLeft; row: StoreRow; need: Exclude<LeftStoreNeed, 'no'>; mode: 'raise' | 'ask' } | null => {
    if (!saved || !leftDecided || !shown || !left || dismissed.has(LEFT_PART)) return null;
    if (lists.read[storeHiringKey(left.storeId)] !== 'ready' || leftRead.state !== 'ready') return null;
    // A list of requests that could not be read cannot say none is waiting.
    if (requests.status === 'failed') return null;
    if (linksOf(left.storeId).length > 0 || requestOf(left.storeId)) return null;
    const row = leftRead.row;
    // The server's own refusals of any raise for the store: not Active, or Outside Approved – Redeploy.
    if (storeRaiseRefusal(row as { store_status?: string | null; redeploy_pending?: boolean | null })) return null;
    const need = leftStoreNeedOf(row, left.personCode);
    if (need === 'no' || !lists.raise.enabled) return null;
    const mode = canRaise ? 'raise' : canAsk ? 'ask' : null;
    return mode ? { left, row, need, mode } : null;
  })();

  // The deploy waits this long for the reads, and no longer.
  useEffect(() => {
    if (stage !== 'asking') return undefined;
    const timer = setTimeout(() => setWaitedOut(true), DEPLOY_HIRING_WAIT_MS);
    return () => clearTimeout(timer);
  }, [stage]);

  // The window before the save: drawn once there is something to show, and then kept until
  // "Deploy" or "Don’t deploy", whatever a read brings meanwhile. A deploy goes ahead by itself
  // only while nothing has been shown: never under the person's eyes.
  const beforeShown = useRef(false);
  const showBefore = stage === 'asking'
    ? !decision && (beforeShown.current || (shown && decided && openParts.length > 0))
    : stage === 'saving' && !!decision?.shown;
  if (showBefore) beforeShown.current = true;

  const decide = useRef(onDecide);
  decide.current = onDecide;
  // Hiring is not shown, the stores have none, a read failed, or the reads are not back in time:
  // the deploy goes ahead at once, with nothing chosen, and no window is drawn before the save.
  const gone = access.state === 'no';
  const nothingBefore = !beforeShown.current && (gone || (decided && openParts.length === 0) || (!decided && waitedOut));
  // Shown and not yet answered, and hiring went under it (switched off, the grant removed, or a
  // window here refused): the window cannot stay, and nothing the person had not pressed is saved.
  // As "Don’t deploy": the page's deploy is as it was, and pressed again it goes at once.
  const goneUnderWindow = beforeShown.current && gone;
  useEffect(() => {
    if (stage !== 'asking' || decision) return;
    if (nothingBefore) {
      setDecision({ shown: false, steps: [] });
      decide.current(true);
    } else if (goneUnderWindow) {
      setDecision({ shown: false, steps: [] });
      decide.current(false);
    }
  }, [stage, decision, nothingBefore, goneUnderWindow]);

  /** "Deploy": what was chosen, as the rights stand now; then the page saves. */
  const deployNow = () => {
    const steps: Step[] = [];
    for (const { store, links, request, asks } of openParts) {
      for (const link of links) {
        const choice = choices[linkKey(link)] ?? 'keep';
        const can = choicesOf(link);
        if ((choice === 'move' || choice === 'cancel' || choice === 'stop') && can[choice]) {
          steps.push({ kind: choice, storeId: store.storeId, storeName: store.storeName, personName: store.personName, link });
        }
      }
      if (request && asks) {
        const choice = choices[requestKey(request)] ?? 'keep';
        if ((choice === 'withdraw' || choice === 'decline') && asks[choice]) {
          steps.push({ kind: choice, storeId: store.storeId, personName: store.personName, request });
        }
      }
    }
    setDecision({ shown: true, steps });
    onDecide(true);
  };

  // Once the deploy is saved: every Stop following at once, then the windows that finish the rest.
  const started = useRef(false);
  useEffect(() => {
    if (!saved || started.current) return;
    started.current = true;
    const steps = decision?.steps ?? [];
    const toStop = steps.filter((s): s is StopStep => s.kind === 'stop');
    const rest = steps.filter((s): s is WindowStep => s.kind !== 'stop');
    if (toStop.length === 0) {
      setQueue(rest);
      return;
    }
    const said = (id: string, line: Omit<StopLine, 'id'>) =>
      setStops((prev) => prev.map((s) => (s.id === id ? { id, ...line } : s)));
    setStops(toStop.map((s) => ({ id: s.link.id, state: 'sending', text: WORDS.stopping(deployOpeningRef(s.link)) })));
    void Promise.all(toStop.map(({ link }) => Promise.resolve()
      .then(() => hiringLinksAPI.release(projectId, link.id))
      .then((res) => {
        const closed = res?.data?.data;
        changedNow.current(closed ?? undefined);
        // A cancel had been sent, and Recruitment no longer listed the opening: it came back cancelled.
        said(link.id, closed?.closedAs === 'cancelled'
          ? { state: 'done', text: CANCEL_WORDS.stopFoundCancelled(closed.openingId ?? link.openingId) }
          : { state: 'done', text: WORDS.stopped(deployOpeningRef(link)) });
      })
      .catch((err: unknown) => {
        if (isHiringOff(err)) {
          hiringGone();
          return;
        }
        const code = hiringCodeOf(err);
        // Someone else stopped or cancelled it first, or a cancel of it is open: the lists are behind.
        const endedElsewhere = code === HIRING_CODES.linkNotLive;
        if (endedElsewhere || code === CANCEL_CODES.cancelInProgress) changedNow.current();
        const why = hiringErrorText(err, '');
        said(link.id, {
          state: 'failed',
          text: why ? `${WORDS.stopFailed(deployOpeningRef(link))} ${why}` : WORDS.stopFailed(deployOpeningRef(link)),
          // Nothing to try again once it is no longer followed.
          next: endedElsewhere ? undefined : WORDS.stopFailedNext,
        });
      })))
      .then(() => setQueue(rest));
    // Run once, with what was decided before the save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  // The next window to open: one at a time, in the order chosen. A request's window only for
  // whoever is still shown requests (it could not have been chosen otherwise).
  const step = (queue ?? []).find((s) => !('request' in s) || access.canSeeRequests) ?? null;
  const stepDone = (finished: WindowStep) => () => setQueue((was) => (was ? was.filter((s) => s !== finished) : was));

  // After the save: the Stop following lines while they are sent; then, every window closed,
  // what became of them and the store left. Nothing (left) to say: the owner lets go.
  const stopsOut = saved && queue === null && stops.length > 0;
  const afterSettled = saved && queue !== null && !step && !sub && leftDecided;
  const nothingAfter = afterSettled && stops.length === 0 && !leftPart;
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (saved && (gone || nothingAfter)) done.current();
  }, [saved, gone, nothingAfter]);

  if (!shown) return null;

  const back = () => setSub(null);

  // A Move, Cancel, Withdraw or Decline chosen before the save, finished in its own window.
  if (saved && step) {
    if ('request' in step) {
      return (
        <HiringRequestWindow
          key={`${step.kind}:${step.request.id}`}
          projectId={projectId}
          // As the list has it now; once it has left the list, as it was chosen.
          request={requests.requests.find((r) => r.id === step.request.id) ?? step.request}
          can={requests.can}
          start={step.kind}
          notice={WORDS.finishLine(step.personName, step.kind, WORDS.theRequest)}
          onClose={stepDone(step)}
          onRequest={(request) => requests.apply(request)}
          onLink={changed}
          onOutOfDate={() => changed()}
          onHiringGone={hiringGone}
          onRaiseGone={raiseGoneNow}
        />
      );
    }
    // The opening as the list has it now; once it has left the list (moved), the window keeps its own copy.
    const now = linksOf(step.storeId).find((l) => l.id === step.link.id);
    return (
      <HiringPanel
        key={`${step.kind}:${step.link.id}`}
        projectId={projectId}
        subject={{ kind: 'store', storeName: step.storeName, storeCode: storeCodeOf([step.link]), storeId: step.storeId }}
        links={now ? [now] : NO_HIRING_LINKS}
        canManage={access.canManage}
        canCancel={access.canRaise && lists.cancel.canCancel}
        start={{ linkId: step.link.id, step: step.kind }}
        notice={WORDS.finishLine(step.personName, step.kind, deployOpeningRef(step.link))}
        onClose={stepDone(step)}
        onChanged={changed}
        onHiringGone={hiringGone}
      />
    );
  }

  if (sub) {
    if (sub.kind === 'store') {
      const links = linksOf(sub.storeId);
      const request = requestOf(sub.storeId);
      return (
        <HiringPanel
          projectId={projectId}
          subject={{ kind: 'store', storeName: sub.storeName, storeCode: storeCodeOf(links), storeId: sub.storeId }}
          links={links}
          canManage={access.canManage}
          canCancel={access.canRaise && lists.cancel.canCancel}
          onClose={back}
          onChanged={changed}
          onHiringGone={hiringGone}
          requests={request ? [request] : NO_REQUESTS}
          onOpenRequest={access.canSeeRequests ? (r) => setSub({ kind: 'request', request: r }) : undefined}
        />
      );
    }
    if (sub.kind === 'request' && access.canSeeRequests) {
      return (
        <HiringRequestWindow
          projectId={projectId}
          // As the list has it now; once it has left the list, as it was opened.
          request={requests.requests.find((r) => r.id === sub.request.id) ?? sub.request}
          can={requests.can}
          onClose={back}
          onRequest={(request) => requests.apply(request)}
          onLink={changed}
          onOutOfDate={() => changed()}
          onHiringGone={hiringGone}
          onRaiseGone={raiseGoneNow}
        />
      );
    }
    if (sub.kind === 'raise' || sub.kind === 'ask') {
      return (
        <RaiseHiringModal
          entry="store"
          mode={sub.kind}
          projectId={projectId}
          subject={sub.subject}
          // A request already being raised for the store is shown in place of a form.
          links={sub.subject.kind === 'store' ? linksOf(sub.subject.storeId) : NO_HIRING_LINKS}
          onClose={back}
          // The window stays open: it is showing the result. This part goes once the store has hiring.
          onChanged={changed}
          onAsked={(request) => requests.apply(request)}
          onOutOfDate={() => changed()}
          onHiringGone={hiringGone}
          onRaiseGone={raiseGoneNow}
          // Recruitment already has an unlinked opening for the store: Link an opening, on it, in place of this window.
          onLinkInstead={sub.kind === 'raise' && access.canManage
            ? ({ budgetId, openingId }) => setSub({ kind: 'link', subject: sub.subject, pick: { budgetId, openingId } })
            : undefined}
        />
      );
    }
    if (sub.kind === 'link' && access.canManage && sub.subject.kind === 'store') {
      const store = sub.subject;
      return (
        <LinkHiringModal
          projectId={projectId}
          subject={{ kind: 'store', storeId: store.storeId, storeName: store.storeName, storeCode: store.storeCode }}
          followed={linksOf(store.storeId)}
          initial={sub.pick}
          onClose={back}
          onLinked={(link) => {
            back();
            changed(link);
          }}
          onOutOfDate={() => changed()}
          onHiringGone={hiringGone}
        />
      );
    }
    return null;
  }

  // ── once saved: what became of each Stop following, and the store left ──
  if (saved) {
    if (!stopsOut && (!afterSettled || nothingAfter)) return null;
    // Not closed while a Stop following is out: the windows chosen to open next would be lost.
    const close = () => {
      if (!stopsOut) onDone();
    };
    return (
      <Modal
        isOpen
        onClose={close}
        title={WORDS.title}
        size="lg"
        footer={<button type="button" className={SECONDARY} disabled={stopsOut} onClick={close}>{WORDS.close}</button>}
      >
        <div className="space-y-4">
          {stops.length > 0 && (
            <section aria-label={WORDS.doneHeading} className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="text-[15px] font-semibold text-gray-900">{WORDS.doneHeading}</h4>
              <ul className="space-y-1.5">
                {stops.map((line) => (
                  <li key={line.id} role="status" className={`text-sm ${line.state === 'failed' ? 'text-red-700' : 'text-gray-800'}`}>
                    {line.text}
                    {line.next && <span className="block text-[13px] text-gray-600">{line.next}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {afterSettled && leftPart && (() => {
            const name = leftPart.left.storeName;
            const heading = leftPart.need === 'short' ? WORDS.needsHeading(name) : WORDS.mayNeedHeading(name);
            const subject = leftSubjectOf(leftPart.row, leftPart.left.storeId, name);
            return (
              <section aria-label={heading} className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
                <div>
                  <h4 className="text-[15px] font-semibold text-gray-900">{heading}</h4>
                  <p className="text-[13px] text-gray-600">{WORDS.movedLine(leftPart.left.personName)}</p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" className={SECONDARY} title={WORDS.notNowHover} onClick={() => setDismissed((was) => new Set([...was, LEFT_PART]))}>
                    {WORDS.notNow}
                  </button>
                  <button
                    type="button"
                    className={PRIMARY}
                    title={leftPart.mode === 'raise' ? WORDS.raiseHover(name) : WORDS.askHover(name)}
                    onClick={() => setSub({ kind: leftPart.mode, subject })}
                  >
                    {leftPart.mode === 'raise' ? WORDS.raise : WORDS.ask}
                  </button>
                </div>
              </section>
            );
          })()}
        </div>
      </Modal>
    );
  }

  // ── before the save ──
  if (!showBefore) return null;
  const saving = stage === 'saving';
  const single = openParts.length === 1 ? openParts[0] : null;
  const headingOf = (part: (typeof openParts)[number]) =>
    (part.links.length > 0 ? WORDS.openHeading(part.store.storeName) : WORDS.askedHeading(part.store.storeName));
  const choose = (key: string) => (value: Choice) => setChoices((was) => ({ ...was, [key]: value }));
  const dontDeploy = () => {
    if (!saving) onDecide(false);
  };

  return (
    <Modal
      isOpen
      // Closing it is "Don’t deploy". Not while the page is saving: the deploy is already out.
      onClose={dontDeploy}
      title={single ? headingOf(single) : WORDS.title}
      size="lg"
      footer={(
        <>
          <button type="button" className={SECONDARY} title={WORDS.dontDeployHover} disabled={saving} onClick={dontDeploy}>
            {WORDS.dontDeploy}
          </button>
          <button type="button" className={PRIMARY} title={openParts.some((p) => p.anyChoice) ? WORDS.deployHover : WORDS.deployHoverPlain} disabled={saving} onClick={deployNow}>
            {saving ? WORDS.deploying : WORDS.deploy}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        {/* Shown, and since then nothing is open there any more (closed from "Open hiring"). */}
        {openParts.length === 0 && <p className="text-sm text-gray-700">{WORDS.nothingOpenNow}</p>}
        {openParts.map((part) => {
          const { store, key, links, request, asks, anyChoice } = part;
          const heading = headingOf(part);
          const keepHover = store.short ? WORDS.keepHover.short : WORDS.keepHover.plain;
          return (
            <section key={key} aria-label={heading} className={single ? 'space-y-3' : 'space-y-3 rounded-lg border border-gray-200 bg-white p-4'}>
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                <div className="min-w-0">
                  {/* One store: the window's title is its heading. */}
                  {!single && <h4 className="text-[15px] font-semibold text-gray-900">{heading}</h4>}
                  <p className="text-[13px] text-gray-600">{WORDS.joinedLine(store.personName, anyChoice ? (links.length > 0 ? 'hiring' : 'request') : null)}</p>
                </div>
                <button
                  type="button"
                  className={OPEN_LINK}
                  title={WORDS.openHiringHover(store.storeName)}
                  disabled={saving}
                  onClick={() => setSub({ kind: 'store', storeId: store.storeId, storeName: store.storeName })}
                >
                  {WORDS.openHiring}
                </button>
              </div>
              <ul className="space-y-2">
                {links.map((link) => {
                  const can = choicesOf(link);
                  const options = [
                    { value: 'keep' as const, label: WORDS.keep, hover: keepHover },
                    ...(can.move ? [{ value: 'move' as const, label: WORDS.move, hover: WORDS.moveHover }] : []),
                    ...(can.cancel ? [{ value: 'cancel' as const, label: WORDS.cancel, hover: WORDS.cancelHover }] : []),
                    ...(can.stop ? [{ value: 'stop' as const, label: WORDS.stop, hover: WORDS.stopHover }] : []),
                  ];
                  // A choice no longer offered (the rights read again since it was picked) is Keep, as "Deploy" takes it.
                  const picked = choices[linkKey(link)] ?? 'keep';
                  return (
                    <li key={link.id} role="group" aria-label={openingTitle(link)} className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-sm text-gray-900">{deployOpeningLine(link)}</p>
                      {options.length > 1 && (
                        <Choices
                          label={WORDS.choicesOf(openingTitle(link))}
                          options={options}
                          value={options.some((o) => o.value === picked) ? picked : 'keep'}
                          onChange={choose(linkKey(link))}
                          disabled={saving}
                        />
                      )}
                    </li>
                  );
                })}
                {request && asks && (() => {
                  const keepRequestHover = store.short ? WORDS.keepRequestHover.short : WORDS.keepRequestHover.plain;
                  const options = [
                    { value: 'keep' as const, label: WORDS.keepRequest, hover: keepRequestHover },
                    ...(asks.withdraw ? [{ value: 'withdraw' as const, label: WORDS.withdraw, hover: WORDS.withdrawHover }] : []),
                    ...(asks.decline ? [{ value: 'decline' as const, label: WORDS.decline, hover: WORDS.declineHover }] : []),
                  ];
                  const picked = choices[requestKey(request)] ?? 'keep';
                  return (
                    <li role="group" aria-label={deployRequestLine(request)} className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-sm text-gray-900">{deployRequestLine(request)}</p>
                      {options.length > 1 && (
                        <Choices
                          label={WORDS.requestChoices}
                          options={options}
                          value={options.some((o) => o.value === picked) ? picked : 'keep'}
                          onChange={choose(requestKey(request))}
                          disabled={saving}
                        />
                      )}
                    </li>
                  );
                })()}
              </ul>
            </section>
          );
        })}
      </div>
    </Modal>
  );
}

export default DeployHiringCheck;
